import { GearboxMarketCompressorV310Abi } from '@1delta/abis'
import { gearboxV3LenderKey } from '@1delta/data-sdk'
import { createMarketUid } from '../../../utils'
import type {
  FetcherResult,
  OraclePriceEntry,
  ParseContext,
} from '../types'

/**
 * Meta shape — empty placeholder; the pool + CM list is discovered lazily
 * from the MarketCompressor response in `parse`, so no call-time metadata
 * is needed.
 */
export interface GearboxV3QueryMeta {
  _: never[]
}

/**
 * Build multicall calls for Gearbox V3.1 oracle prices.
 *
 * Currently a no-op (`[]`). The public-data fetcher at
 * `lending/public-data/gearbox/` already runs a MarketCompressor call per
 * chain — emitting duplicate oracle-side calls here would double RPC
 * cost without adding information. When oracle prices need to diverge
 * from public-data (e.g. push a different USD valuation basis), wire
 * this to its own `MarketCompressor.getMarkets` call via the
 * per-curator list from `gearboxMarketConfigurators(chainId)`.
 */
export function getGearboxV3Calls(
  _chainId: string,
): FetcherResult<GearboxV3QueryMeta>[] {
  return []
}

/**
 * Parse Gearbox V3.1 oracle results — emit per-(CM, token) price entries.
 *
 * Input shape matches `MarketCompressor.getMarkets` → `MarketData[]`
 * where each market's `creditManagers[i]` carries a
 * `{creditManager.collateralTokens: [{token, liquidationThreshold}]}`
 * list. We emit:
 *   - one "base" entry for the pool underlying with `staticBase: true`
 *   - one collateral entry per collateralToken with `priceUSD` resolved
 *     from `context.usdPrices`.
 *
 * Targets are the per-CM lender keys synthesized by `gearboxV3LenderKey`.
 * Expired or paused CMs are skipped.
 */
export function parseGearboxV3Results(
  data: any[],
  _meta: GearboxV3QueryMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId, usdPrices, tokenList } = context
  const entries: OraclePriceEntry[] = []
  const now = Math.floor(Date.now() / 1000)

  const markets = data[0]
  if (!Array.isArray(markets) || markets.length === 0) return entries

  const resolvePriceUSD = (token: string): number | undefined => {
    const key = tokenList?.[token]?.assetGroup ?? `${chainId}-${token}`
    return usdPrices[key] ?? usdPrices[token]
  }

  for (const m of markets) {
    try {
      const pool = m?.pool
      if (!pool || pool.isPaused) continue

      const underlying = (pool.underlying ?? '').toString().toLowerCase()
      if (!underlying) continue
      const underlyingUSD = resolvePriceUSD(underlying)
      if (!underlyingUSD) continue

      for (const cm of m.creditManagers ?? []) {
        const facade = cm?.creditFacade
        const manager = cm?.creditManager
        if (!facade || !manager) continue

        // Skip expired / paused CMs.
        const expDate = Number(facade.expirationDate ?? 0)
        if (facade.isPaused || (expDate > 0 && expDate <= now)) continue

        const cmAddr = (manager.baseParams?.addr ?? '')
          .toString()
          .toLowerCase()
        if (!cmAddr) continue

        let lenderKey: string
        try {
          lenderKey = gearboxV3LenderKey(cmAddr)
        } catch {
          continue
        }

        // Loan-side (pool underlying) — base asset of the CM.
        entries.push({
          asset: underlying,
          price: 1,
          priceUSD: underlyingUSD,
          marketUid: createMarketUid(chainId, lenderKey, underlying),
          targetLender: lenderKey,
          description: `Gearbox V3 loan (${pool.symbol ?? ''})`,
          staticBase: true,
          baseAsset: underlying,
        })

        // Collateral tokens — price each one from the global USD map.
        for (const ct of manager.collateralTokens ?? []) {
          const token = (ct?.token ?? '').toString().toLowerCase()
          if (!token || token === underlying) continue
          const tokenUSD = resolvePriceUSD(token)
          if (!tokenUSD) continue

          entries.push({
            asset: token,
            price: tokenUSD / underlyingUSD,
            priceUSD: tokenUSD,
            marketUid: createMarketUid(chainId, lenderKey, token),
            targetLender: lenderKey,
            baseAsset: underlying,
          })
        }
      }
    } catch {
      // Skip malformed market records
    }
  }

  return entries
}

export function getGearboxV3Abi() {
  return GearboxMarketCompressorV310Abi
}

/** Gearbox V3.1 Oracle Fetcher module */
export const gearboxV3Fetcher = {
  getCalls: getGearboxV3Calls,
  parse: parseGearboxV3Results,
  getAbi: getGearboxV3Abi,
}
